import React, { useState } from 'react';

export default function Whitepaper() {
  const [activeSection, setActiveSection] = useState('abstract');

  const sections = [
    { id: 'abstract',      label: '§0 Abstract' },
    { id: 'intro',         label: '§1 Introduction' },
    { id: 'tokenomics',    label: '§2 Tokenomics' },
    { id: 'pi',            label: '§3 PI Controller' },
    { id: 'oracle',        label: '§4 Oracle System' },
    { id: 'governance',    label: '§5 Governance' },
    { id: 'security',      label: '§6 Security' },
    { id: 'roadmap',       label: '§7 Roadmap' },
    { id: 'contracts',     label: '§8 Contracts' },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 80px', display: 'flex', gap: 32 }}>

      {/* ── Sidebar TOC ── */}
      <div style={{ width: 220, flexShrink: 0 }}>
        <div style={{ position: 'sticky', top: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
            Table of Contents
          </div>
          {sections.map(s => (
            <button key={s.id} onClick={() => {
              setActiveSection(s.id);
              document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
              borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, marginBottom: 2,
              background: activeSection === s.id ? 'rgba(247,147,26,0.15)' : 'transparent',
              color: activeSection === s.id ? '#f7931a' : '#64748b',
              fontWeight: activeSection === s.id ? 700 : 400,
              transition: 'all 0.15s',
            }}>
              {s.label}
            </button>
          ))}
          <div style={{ marginTop: 24, padding: 12, background: 'rgba(247,147,26,0.06)', borderRadius: 10, border: '1px solid rgba(247,147,26,0.15)' }}>
            <div style={{ fontSize: 11, color: '#f7931a', fontWeight: 700, marginBottom: 6 }}>VERSION</div>
            <div style={{ fontSize: 13, color: '#94a3b8' }}>v1.0 — April 2026</div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>Sepolia Testnet</div>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Title */}
        <div style={{ textAlign: 'center', padding: '40px 0 48px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>₿²</div>
          <h1 style={{ fontSize: 36, fontWeight: 900, color: '#fff', margin: '0 0 8px', letterSpacing: -1 }}>
            Twin Bitcoin <span style={{ color: '#f7931a' }}>(TBC)</span>
          </h1>
          <p style={{ fontSize: 16, color: '#64748b', margin: '0 0 16px' }}>
            A Scarcity-Anchored, Adaptive-Supply Cryptocurrency with On-Chain PI Control
          </p>
          <div style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            {['ERC-20', 'Sepolia', 'Chainlink Oracle', 'Quadratic Governance', 'PI Controller'].map(tag => (
              <span key={tag} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.06)', color: '#94a3b8', fontWeight: 600 }}>{tag}</span>
            ))}
          </div>
        </div>

        {/* §0 Abstract */}
        <Section id="abstract" title="§0 Abstract" onVisible={setActiveSection}>
          <p>
            Twin Bitcoin (TBC) is an ERC-20 cryptocurrency deployed on Ethereum with a hard supply cap of
            <strong style={{ color: '#f7931a' }}> 42,000,000 TBC</strong> — exactly twice Bitcoin's 21 million limit.
            Unlike Bitcoin's fixed issuance schedule, TBC employs a real-time <strong style={{ color: '#f7931a' }}>Proportional-Integral (PI) Controller</strong> that
            autonomously adjusts circulating supply in response to live market price data sourced from
            <strong style={{ color: '#3b82f6' }}> Chainlink's BTC/USD price feed</strong>.
          </p>
          <p>
            The system targets a stable price equilibrium using control theory mathematics, minting TBC when
            price is too high (expanding supply to push price down) and burning TBC when price is too low
            (contracting supply to push price up). All parameter changes are governed by token holders
            through an on-chain <strong style={{ color: '#10b981' }}>quadratic voting governance</strong> contract,
            preventing whale dominance.
          </p>
          <Callout color="#f7931a">
            TBC combines the scarcity of Bitcoin with the adaptive stability of algorithmic supply control —
            anchored to real-world BTC price data via decentralised oracle infrastructure.
          </Callout>
        </Section>

        {/* §1 Introduction */}
        <Section id="intro" title="§1 Introduction" onVisible={setActiveSection}>
          <h4 style={h4Style}>1.1 Problem Statement</h4>
          <p>
            Bitcoin's fixed supply schedule creates extreme price volatility — it cannot respond to changes
            in demand. Algorithmic stablecoins have failed catastrophically (UST/LUNA) because they lack
            hard supply caps and real price anchors. Existing governance tokens concentrate power in large holders.
          </p>
          <h4 style={h4Style}>1.2 TBC's Solution</h4>
          <p>TBC addresses each failure mode:</p>
          <ul style={ulStyle}>
            <li><strong style={{ color: '#f7931a' }}>Hard cap of 42M TBC</strong> — mathematically enforced in the smart contract. No governance vote can override this.</li>
            <li><strong style={{ color: '#f7931a' }}>PI Controller supply management</strong> — uses control theory (not speculation) to stabilise price toward target.</li>
            <li><strong style={{ color: '#f7931a' }}>Chainlink oracle anchoring</strong> — price data is sourced from the world's most battle-tested decentralised oracle network.</li>
            <li><strong style={{ color: '#f7931a' }}>Quadratic governance</strong> — voting weight = √(staked TBC), so 10,000 TBC gets 100 votes, not 10,000 votes.</li>
          </ul>
          <h4 style={h4Style}>1.3 Name Origin</h4>
          <p>
            "Twin Bitcoin" reflects two properties: (1) TBC's supply cap is exactly 2× Bitcoin's, and
            (2) TBC's price mechanism is designed to track and mirror BTC's movements while providing
            supply-based dampening of extreme volatility.
          </p>
        </Section>

        {/* §2 Tokenomics */}
        <Section id="tokenomics" title="§2 Tokenomics" onVisible={setActiveSection}>
          <h4 style={h4Style}>2.1 Supply Parameters</h4>
          <Table rows={[
            ['Parameter', 'Value', 'Notes'],
            ['Max Supply (S_MAX)', '42,000,000 TBC', 'Hard-coded, immutable'],
            ['Genesis Supply', '18,400,000 TBC', '43.8% of max — minted at deployment'],
            ['Remaining Mintable', '23,600,000 TBC', 'Released only by PI controller'],
            ['Decimals', '18', 'Same as ETH standard'],
            ['Token Standard', 'ERC-20', 'Ethereum compatible'],
          ]} />

          <h4 style={h4Style}>2.2 Supply Distribution</h4>
          <p>
            The genesis supply of 18.4M TBC represents the "Bitcoin equivalent" at launch — comparable
            to Bitcoin's circulating supply at a similar stage of adoption. The remaining 56.2% can only
            be released by the PI controller minting in response to price signals, ensuring supply growth
            is always demand-driven.
          </p>
          <Callout color="#10b981">
            Unlike ICO tokens, there is no team allocation, no pre-mine beyond genesis, and no vesting
            cliff. All new TBC must be earned through on-chain price stability mechanisms.
          </Callout>

          <h4 style={h4Style}>2.3 Supply Mechanics</h4>
          <ul style={ulStyle}>
            <li><strong>Mint:</strong> PI Controller calls <code style={codeStyle}>controllerMint()</code> when price {">"} target → expanding supply lowers price</li>
            <li><strong>Burn:</strong> PI Controller calls <code style={codeStyle}>controllerBurn()</code> when price {"<"} target → contracting supply raises price</li>
            <li><strong>Hard Stop:</strong> <code style={codeStyle}>_update()</code> override reverts any mint that would exceed S_MAX</li>
          </ul>
        </Section>

        {/* §3 PI Controller */}
        <Section id="pi" title="§3 PI Controller" onVisible={setActiveSection}>
          <h4 style={h4Style}>3.1 Control Theory Overview</h4>
          <p>
            A Proportional-Integral (PI) controller is a feedback loop mechanism from control engineering,
            used in everything from thermostats to rocket engines. TBC applies this to token supply:
          </p>
          <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: '20px 24px', margin: '16px 0', fontFamily: 'monospace', fontSize: 14, color: '#93c5fd' }}>
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>{'// Supply adjustment formula'}</div>
            <div>error = currentPrice - targetPrice (1,000,000 = $1.00)</div>
            <div style={{ marginTop: 8 }}>ΔS = α · error</div>
            <div style={{ paddingLeft: 24 }}>+ β · velocity {'(rate of price change)'}</div>
            <div style={{ paddingLeft: 24 }}>+ γ · integral {'(accumulated error)'}</div>
            <div style={{ marginTop: 8 }}>{'ΔS = clamp(ΔS, -ρ · S, +ρ · S)  // rate limit'}</div>
          </div>

          <h4 style={h4Style}>3.2 Controller Parameters</h4>
          <Table rows={[
            ['Parameter', 'Value', 'Role'],
            ['α (alpha)', '0.0200', 'Proportional gain — reacts to current error'],
            ['β (beta)', '0.0100', 'Velocity gain — dampens oscillation'],
            ['γ (gamma)', '0.0050', 'Integral gain — eliminates steady-state error'],
            ['ε (epsilon)', '0.1500', 'Anti-windup limit for integral'],
            ['ρ (rho)', '0.0005', 'Max supply change per epoch — IMMUTABLE'],
            ['Epoch length', '720 blocks', '~2.4 hours at 12s/block'],
          ]} />

          <h4 style={h4Style}>3.3 Rate Limiting (ρ)</h4>
          <p>
            The rho parameter is <strong style={{ color: '#ef4444' }}>immutable</strong> — it cannot be changed by governance.
            It caps any single epoch's supply adjustment at 0.05% of current supply, preventing
            catastrophic mints or burns even if the oracle is manipulated.
          </p>
          <Callout color="#ef4444">
            Maximum single-epoch supply change = ρ × S = 0.0005 × 18,400,000 = ~9,200 TBC.
            This is a hard engineering constraint, not a governance parameter.
          </Callout>

          <h4 style={h4Style}>3.4 Epoch Settlement</h4>
          <p>
            Anyone can trigger epoch settlement by calling <code style={codeStyle}>oracle.settleEpochWithOracle()</code>.
            The oracle fetches live BTC/USD from Chainlink, derives a TBC price, and passes it to the
            PI controller. Settlement is rate-limited to once per 720 blocks (~2.4 hours).
          </p>
        </Section>

        {/* §4 Oracle */}
        <Section id="oracle" title="§4 Oracle System" onVisible={setActiveSection}>
          <h4 style={h4Style}>4.1 Architecture</h4>
          <p>
            TBC uses <strong style={{ color: '#3b82f6' }}>Chainlink's BTC/USD AggregatorV3</strong> feed on Sepolia
            (address: <code style={codeStyle}>0x1b44F3514812d835EB1BDB0acB33d3fA3351Ee43</code>).
            The TBCOracle contract acts as a bridge between the raw 8-decimal Chainlink price and
            the 6-decimal PI controller format.
          </p>

          <h4 style={h4Style}>4.2 Price Derivation</h4>
          <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: '20px 24px', margin: '16px 0', fontFamily: 'monospace', fontSize: 14, color: '#93c5fd' }}>
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>{'// Oracle price scaling'}</div>
            <div>{'btcPrice8  = Chainlink answer (8 decimals)'}</div>
            <div>{'btcPrice6  = btcPrice8 / 100    // convert to 6 decimals'}</div>
            <div style={{ marginTop: 8 }}>{'// TBC is priced as inverse of BTC, normalised to $1 target:'}</div>
            <div>{'tbcPrice   = (1e6 × 1e6) / btcPrice6'}</div>
            <div style={{ marginTop: 8, color: '#64748b' }}>{'// Example: BTC = $100,000'}</div>
            <div style={{ color: '#64748b' }}>{'// btcPrice6 = 100,000,000,000 → tbcPrice = 10'}</div>
          </div>

          <h4 style={h4Style}>4.3 Safety Mechanisms</h4>
          <ul style={ulStyle}>
            <li><strong>Stale price check:</strong> Reverts if Chainlink data is {">"} 3 hours old</li>
            <li><strong>Negative price guard:</strong> Reverts if <code style={codeStyle}>answer {"<="} 0</code></li>
            <li><strong>Round completeness:</strong> Checks <code style={codeStyle}>answeredInRound {"≥"} roundId</code></li>
            <li><strong>Price clamping:</strong> tbcPrice clamped to [1, 1,000,000,000] before passing to PI</li>
          </ul>
        </Section>

        {/* §5 Governance */}
        <Section id="governance" title="§5 Governance" onVisible={setActiveSection}>
          <h4 style={h4Style}>5.1 Quadratic Voting</h4>
          <p>
            Standard token governance is plutocratic — a whale with 1M tokens gets 1M votes.
            TBC uses <strong style={{ color: '#10b981' }}>quadratic voting</strong> to limit this:
          </p>
          <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '20px 24px', margin: '16px 0', fontFamily: 'monospace', fontSize: 14, color: '#6ee7b7' }}>
            <div>{'votingWeight = floor( √(stakedBalance / 1e18) )'}</div>
            <div style={{ marginTop: 8, color: '#64748b' }}>{'// 100 TBC staked → 10 votes'}</div>
            <div style={{ color: '#64748b' }}>{'// 10,000 TBC staked → 100 votes'}</div>
            <div style={{ color: '#64748b' }}>{'// 1,000,000 TBC staked → 1,000 votes'}</div>
          </div>
          <p>
            This means doubling your stake does not double your influence — it only increases it by ~41%.
            Large holders still have more power, but community members are not powerless.
          </p>

          <h4 style={h4Style}>5.2 Proposal Lifecycle</h4>
          <Table rows={[
            ['State', 'Condition', 'Duration'],
            ['Pending', 'Proposal created', '~5 blocks (delay)'],
            ['Active', 'Voting open', '~720 blocks (~2.4h)'],
            ['Succeeded / Defeated', 'Voting closed', 'Instant'],
            ['Queued', 'Passed, waiting', '~720 blocks (timelock)'],
            ['Executed', 'Timelock elapsed', 'Permanent'],
          ]} />

          <h4 style={h4Style}>5.3 Proposal Types</h4>
          <ul style={ulStyle}>
            <li><strong>GENERAL</strong> — Community discussions, off-chain actions</li>
            <li><strong>UPDATE_PI_PARAMS</strong> — Adjust alpha, beta, gamma, epsilon</li>
            <li><strong>UPDATE_GOV_PARAMS</strong> — Change voting period, quorum, proposal delay</li>
            <li><strong>TRANSFER_FUNDS</strong> — Move treasury funds</li>
          </ul>
          <Callout color="#10b981">
            Minimum stake to propose: 1,000 TBC. This prevents spam proposals while keeping the
            bar low enough for genuine community participants.
          </Callout>
        </Section>

        {/* §6 Security */}
        <Section id="security" title="§6 Security" onVisible={setActiveSection}>
          <h4 style={h4Style}>6.1 Smart Contract Protections</h4>
          <ul style={ulStyle}>
            <li><strong>OpenZeppelin Ownable:</strong> Oracle and governance use battle-tested access control</li>
            <li><strong>Immutable S_MAX:</strong> Hard cap is a constant — no function can change it</li>
            <li><strong>Immutable ρ (rho):</strong> Rate limit cannot be changed even by governance</li>
            <li><strong>_update() override:</strong> Every mint is checked against S_MAX at the token level</li>
            <li><strong>Reentrancy safety:</strong> All state changes before external calls (CEI pattern)</li>
            <li><strong>viaIR compilation:</strong> Stack-too-deep protection, better optimizer output</li>
          </ul>

          <h4 style={h4Style}>6.2 Oracle Attack Surface</h4>
          <p>
            The main oracle risk is a Chainlink price manipulation. TBC mitigates this with:
          </p>
          <ul style={ulStyle}>
            <li>3-hour staleness check — rejects old data</li>
            <li>ρ rate limit — even a 100× wrong price can only move supply by 0.05% per epoch</li>
            <li>Price clamping — tbcPrice bounded to [1, 1B] before PI calculation</li>
          </ul>

          <h4 style={h4Style}>6.3 Governance Attack Surface</h4>
          <ul style={ulStyle}>
            <li>Timelock between Succeeded → Executed gives community time to react</li>
            <li>Quadratic voting reduces flash-loan governance attacks</li>
            <li>Minimum stake requirement prevents spam proposals</li>
          </ul>
          <Callout color="#ef4444">
            TBC is currently deployed on <strong>Sepolia testnet</strong>. No real funds are at risk.
            A full mainnet audit is recommended before production deployment.
          </Callout>
        </Section>

        {/* §7 Roadmap */}
        <Section id="roadmap" title="§7 Roadmap" onVisible={setActiveSection}>
          {[
            { phase: 'Phase 1', title: 'Testnet Launch', status: 'done', items: ['Deploy TBC, PI Controller, Governance on Sepolia', 'Chainlink BTC/USD oracle integration', 'React dashboard with live contract data', 'Uniswap V3 liquidity pool (TBC/WETH)', 'Governance proposals UI', 'Etherscan contract verification'] },
            { phase: 'Phase 2', title: 'Audit & Hardening', status: 'next', items: ['Independent smart contract audit', 'Formal verification of PI Controller bounds', 'Bug bounty program', 'Multi-sig for initial governance actions', 'Keeper network for automated epoch settlement'] },
            { phase: 'Phase 3', title: 'Mainnet Launch', status: 'future', items: ['Ethereum mainnet deployment', 'CoinGecko / CoinMarketCap listing', 'Centralised exchange outreach', 'Expanded Uniswap liquidity', 'Cross-chain bridge exploration (Base, Arbitrum)'] },
            { phase: 'Phase 4', title: 'Ecosystem Growth', status: 'future', items: ['TBC lending markets (Aave / Compound integration)', 'Yield farming incentives for liquidity providers', 'DAO treasury management', 'Mobile wallet support', 'Developer SDK and documentation'] },
          ].map(({ phase, title, status, items }) => (
            <div key={phase} style={{ marginBottom: 24, padding: 20, borderRadius: 14, background: status === 'done' ? 'rgba(16,185,129,0.06)' : status === 'next' ? 'rgba(247,147,26,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${status === 'done' ? 'rgba(16,185,129,0.2)' : status === 'next' ? 'rgba(247,147,26,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>{status === 'done' ? '✅' : status === 'next' ? '🔄' : '🔮'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: status === 'done' ? '#10b981' : status === 'next' ? '#f7931a' : '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>{phase}</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{title}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 6, background: status === 'done' ? 'rgba(16,185,129,0.15)' : status === 'next' ? 'rgba(247,147,26,0.15)' : 'rgba(255,255,255,0.05)', color: status === 'done' ? '#10b981' : status === 'next' ? '#f7931a' : '#475569' }}>
                  {status === 'done' ? 'COMPLETE' : status === 'next' ? 'IN PROGRESS' : 'PLANNED'}
                </span>
              </div>
              <ul style={{ ...ulStyle, margin: 0 }}>
                {items.map(item => <li key={item} style={{ color: status === 'done' ? '#94a3b8' : '#64748b' }}>{item}</li>)}
              </ul>
            </div>
          ))}
        </Section>

        {/* §8 Contracts */}
        <Section id="contracts" title="§8 Deployed Contracts" onVisible={setActiveSection}>
          <p style={{ color: '#64748b', marginBottom: 20 }}>
            All contracts are deployed and verified on <strong style={{ color: '#3b82f6' }}>Ethereum Sepolia</strong> testnet.
          </p>
          {[
            { name: 'TBC Token', addr: '0x9d47Db99CEad9F907E0FF99326452C25C8708ACF', desc: 'ERC-20 token with hard cap enforcement' },
            { name: 'TBCPIController', addr: '0xdBE9FBc2584978b1eeA4844bD33a1FE064720520', desc: 'Adaptive supply PI controller' },
            { name: 'TBCGovernance', addr: '0x9c36e515bd49AC146f3060e34eccC086007EECa6', desc: 'Quadratic voting governance' },
            { name: 'TBCOracle', addr: '0xBDEC1a10925AF10790233F87194EB20f85Db9b54', desc: 'Chainlink BTC/USD → PI bridge' },
          ].map(c => (
            <div key={c.addr} style={{ padding: '14px 18px', marginBottom: 10, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', marginBottom: 2 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{c.desc}</div>
                </div>
                <a href={`https://sepolia.etherscan.io/address/${c.addr}`} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, fontFamily: 'monospace', color: '#3b82f6', textDecoration: 'none', wordBreak: 'break-all' }}>
                  {c.addr} ↗
                </a>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 32, padding: 20, borderRadius: 12, background: 'rgba(247,147,26,0.05)', border: '1px solid rgba(247,147,26,0.15)', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7 }}>
              Source code available on{' '}
              <a href="https://github.com" target="_blank" rel="noreferrer" style={{ color: '#f7931a' }}>GitHub</a>
              {' · '}
              Frontend at{' '}
              <a href="https://tbc-frontend-theta.vercel.app" target="_blank" rel="noreferrer" style={{ color: '#f7931a' }}>tbc-frontend-theta.vercel.app</a>
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Section({ id, title, children, onVisible }: {
  id: string; title: string; children: React.ReactNode;
  onVisible: (id: string) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onVisible(id); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [id, onVisible]);

  return (
    <div id={id} ref={ref} style={{ marginBottom: 56 }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {title}
      </h2>
      <div style={{ fontSize: 15, color: '#94a3b8', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>
  );
}

function Callout({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <div style={{ margin: '20px 0', padding: '14px 18px', borderRadius: 10, background: `${color}11`, borderLeft: `3px solid ${color}`, fontSize: 14, color: '#94a3b8', lineHeight: 1.7 }}>
      {children}
    </div>
  );
}

function Table({ rows }: { rows: string[][] }) {
  const [header, ...body] = rows;
  return (
    <div style={{ overflowX: 'auto', margin: '16px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: '10px 14px', color: ci === 0 ? '#e2e8f0' : '#94a3b8' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const h4Style: React.CSSProperties = {
  fontSize: 15, fontWeight: 700, color: '#e2e8f0', margin: '24px 0 10px',
};
const ulStyle: React.CSSProperties = {
  margin: '10px 0', paddingLeft: 20, lineHeight: 2,
};
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 13, background: 'rgba(255,255,255,0.07)',
  padding: '1px 6px', borderRadius: 4, color: '#93c5fd',
};
